import {
  Box,
  Button,
  Card,
  Divider,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { saveAs } from 'file-saver';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from 'src/components/atoms/loading-button/loading-button';
import { Query } from 'src/components/atoms/query';
import { HintBox } from 'src/components/molecules/hint-box';
import { PageHeader } from 'src/components/molecules/page-header';
import { Pagination } from 'src/components/organisms/pagination';
import { employeesKeys } from 'src/features/employees/api/query-keys/employees';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSelection } from 'src/hooks/use-selection';
import { useSortController } from 'src/hooks/use-sort-controller';
import { DownloadIcon } from 'src/icons/download';
import { Plus } from 'src/icons/plus';
import { Can, withPermissions } from 'src/permissions/can';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { convertBase64ToBlob } from 'src/utils/convert-base64-to-blob';
import { useDownloadEmployees } from '../../api/hooks/use-download-employees';
import { fetchEmployees } from '../../api/lib/employees';
import EmployeesActionsTableMenu from '../molecules/employees-actions-table-menu';
import { EmployeesBulkActionsBubble } from '../molecules/employees-bulk-actions-bubble';
import { EmployeeStatusFilterPopover } from '../molecules/filters/employee-status-filter-popover';
import { EmployeesFilterChips } from '../molecules/filters/employees-filter-chips';
import { ChangeEmployeeGroupDialog } from '../organisms/dialogs/change-employee-group-dialog';
import { DeleteEmployeeDialog } from '../organisms/dialogs/delete-employee-dialog';
import { DeleteEmployeesDialog } from '../organisms/dialogs/delete-employees-dialog';
import { InviteEmployeeDialog } from '../organisms/dialogs/invite-employee-dialog';
import UploadEmployeesDialog from '../organisms/dialogs/upload-employees-dialog';
import EmployeesTable from '../organisms/employees-table';
import { EmployeesActionsSlotPayload } from '../organisms/employees-table-row';

const Employees = () => {
  const { t } = useTranslation();
  const { directoryToken } = useUserStore();

  const { query, perPage, page, filter } = useSearch({
    from: '/_layout/tenant/employees/_layout/',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const [sortController, setSortController] = useSortController({
    sortBy: 'email',
    sortOrder: SortOrder.DESC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
    };
  }, [sortController.sortBy, sortController.sortOrder]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeSort,
    handleChangeFilterValue,
  } = useMeilisearchWithUrlParams(
    fetchEmployees,
    employeesKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  const {
    mutateAsync: downloadEmployees,
    isPending: isDownloadEmployeesPending,
  } = useDownloadEmployees();

  // Invite new employee
  const [
    isOpenInviteEmployeeDialog,
    handleOpenInviteEmployeeDialog,
    handleCloseInviteEmployeeDialog,
  ] = useDialog();

  // Upload employees list
  const [
    isOpenUploadEmployeesDialog,
    handleOpenUploadEmployeesDialog,
    handleCloseUploadEmployeesDialog,
  ] = useDialog();

  // Delete employee
  const [
    isOpenDeleteEmployeeDialog,
    handleOpenDeleteEmployeeDialog,
    handleCloseDeleteEmployeeDialog,
  ] = useDialog();

  // Delete employees bulk
  const [
    isOpenDeleteEmployeesDialog,
    handleOpenDeleteEmployeesDialog,
    handleCloseDeleteEmployeesDialog,
  ] = useDialog();

  // Change employee group
  const [
    isOpenChangeEmployeeGroupDialog,
    handleOpenChangeEmployeeGroupDialog,
    handleCloseChangeEmployeeGroupDialog,
  ] = useDialog();

  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeesActionsSlotPayload | null>(null);

  const [
    selectedEmployeesTokens,
    previousSelectedEmployeesTokens,
    toggleSelection,
    toggleAllSelection,
    handleClearSelected,
    handlePreviousRowSelection,
  ] = useSelection<string>();

  const actionsSlot = useCallback(
    ({ token, email, employeeGroupToken }: EmployeesActionsSlotPayload) => (
      <EmployeesActionsTableMenu
        onChangeGroup={() => {
          setSelectedEmployee({ token, email, employeeGroupToken });
          handleOpenChangeEmployeeGroupDialog();
        }}
        onDelete={() => {
          setSelectedEmployee({ token, email, employeeGroupToken });
          handleOpenDeleteEmployeeDialog();
        }}
      />
    ),
    [],
  );

  const handleDownloadEmployees = async () => {
    const { content, name } = await downloadEmployees();

    const blob = convertBase64ToBlob(content, 'application/vnd.ms-excel');

    saveAs(blob, name);
  };

  const isAllEmployeesFromCurrentPageSelected =
    results?.hits?.every(employee =>
      selectedEmployeesTokens.includes(employee.token),
    ) ?? false;

  return (
    <>
      <PageHeader
        title={t('employees:employees.title')}
        extras={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!isLoading ? (
              <Typography variant="body2">
                {t('employees:employees.totalAmount', {
                  amount: results?.estimatedTotalHits ?? 0,
                })}
              </Typography>
            ) : (
              <Skeleton height={42} width={200} />
            )}

            <Can I="create" a="Employees" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      sx={{ ml: 2 }}
                      onClick={handleOpenInviteEmployeeDialog}
                      color="primary"
                      disabled={!allowed}
                      startIcon={<Plus fontSize="small" />}
                      variant="outlinedContained"
                      id="invite_employee_button"
                      size="large">
                      {t('employees:employees.inviteButton')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>

            <Can I="upload" a="Employees" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      sx={{ ml: 2 }}
                      onClick={handleOpenUploadEmployeesDialog}
                      color="primary"
                      disabled={!allowed}
                      startIcon={<Plus fontSize="small" />}
                      variant="contained"
                      id="upload_employees_button"
                      size="large">
                      {t('employees:employees.uploadEmployeesButton')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
          </Box>
        }
      />

      <HintBox>
        <Typography variant="body2" color="textSecondary">
          {t('employees:employees.hint')}
        </Typography>
      </HintBox>

      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          height: '100%',
        }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 3,
            py: 2.5,
          }}>
          <Query
            searchAsType
            disabled={false}
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={query}
          />

          <LoadingButton
            variant="outlined"
            loading={isDownloadEmployeesPending}
            onClick={handleDownloadEmployees}
            startIcon={<DownloadIcon />}
            sx={{ minWidth: 165 }}>
            {t('employees:employees.downloadData')}
          </LoadingButton>

          <EmployeeStatusFilterPopover
            filters={filter}
            handleChangeFilterValue={handleChangeFilterValue}
          />
        </Box>

        <EmployeesFilterChips
          filters={filter}
          handleChangeFilterValue={handleChangeFilterValue}
        />

        <EmployeesTable
          data={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message}
          sortController={sortController}
          setSortController={setSortController}
          handleChangeSort={handleChangeSort}
          actionsSlot={actionsSlot}
          isAllEmployeesFromCurrentPageSelected={
            isAllEmployeesFromCurrentPageSelected
          }
          toggleAllSelection={toggleAllSelection}
          selectedEmployees={selectedEmployeesTokens}
          toggleSelection={toggleSelection}
        />

        <Divider sx={{ mt: 'auto' }} />

        <Pagination
          disabled={isLoading}
          isFetching={isFetching}
          changePage={handleChangePage}
          rowsCount={results?.estimatedTotalHits || 0}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          handlePerPageChange={handleChangeLimit}
        />
      </Card>

      <EmployeesBulkActionsBubble
        selectedEmployees={selectedEmployeesTokens}
        previousSelectedEmployees={previousSelectedEmployeesTokens}
        onDelete={handleOpenDeleteEmployeesDialog}
        allItemsCount={results?.hits?.length ?? 0}
        handleClearSelected={handleClearSelected}
        toggleAllSelection={toggleAllSelection}
        allEmployeesTokens={
          results?.hits?.map(employee => employee.token) ?? []
        }
        handlePreviousRowSelection={handlePreviousRowSelection}
        isAllEmployeesFromCurrentPageSelected={
          isAllEmployeesFromCurrentPageSelected
        }
      />

      <InviteEmployeeDialog
        open={isOpenInviteEmployeeDialog}
        onCancel={handleCloseInviteEmployeeDialog}
      />

      <UploadEmployeesDialog
        open={isOpenUploadEmployeesDialog}
        onCancel={handleCloseUploadEmployeesDialog}
      />

      <DeleteEmployeeDialog
        open={isOpenDeleteEmployeeDialog}
        onCancel={() => {
          handleCloseDeleteEmployeeDialog();
          setSelectedEmployee(null);
        }}
        selectedEmployee={selectedEmployee}
      />

      <DeleteEmployeesDialog
        open={isOpenDeleteEmployeesDialog}
        onCancel={() => {
          handleCloseDeleteEmployeesDialog();
          handleClearSelected();
        }}
        selectedEmployeesTokens={selectedEmployeesTokens}
      />

      <ChangeEmployeeGroupDialog
        open={isOpenChangeEmployeeGroupDialog}
        onCancel={handleCloseChangeEmployeeGroupDialog}
        selectedEmployeeToken={selectedEmployee?.token ?? ''}
        selectedEmployeeGroupToken={selectedEmployee?.employeeGroupToken ?? ''}
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'Employees'),
  Employees,
);
